/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react'
import { DragDropProvider } from '@dnd-kit/react'
import { isSortable } from '@dnd-kit/react/sortable'
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers'
import { arrayMove } from '@dnd-kit/helpers'
import {
    PointerSensor,
    PointerActivationConstraints,
    AutoScroller,
} from '@dnd-kit/dom'
import { StatPickerProps, StatGroup, PlayerRow } from './utils/types.ts'
import {
    prettyStatNames,
    groupStatSelects,
    DEFAULT_BATTING_STATS,
    DEFAULT_PITCHING_STATS,
    DEFAULT_CATCHING_STATS,
} from './utils/stat-groupings.ts'
import SortableWrapper from './SortableWrapper.tsx'
import RemoveButton from './RemoveButton.tsx'
import { isStatsFull } from './utils/helpers.ts'
import { useDimensions } from './utils/responsive-dimensions.tsx'
import {
    STAT_DRAG_RECT_WIDTH,
    REMOVE_BUTTON_X,
    CLICK_AREA_WIDTH,
    TRUNCATE_TEXT_LENGTH,
    TRUNCATE_TEXT_CUTOFF,
} from './utils/constants.ts'

export default function StatPicker({
    statRef,
    stats,
    selected,
    currentType,
    showTitle,
    showTeams,
    onAdd,
}: StatPickerProps) {
    const [searchFilter, setSearchFilter] = useState('')
    const [draftSelected, setDraftSelected] = useState(selected)
    const [flashingStat, setFlashingStat] = useState<string | null>(null)

    const groupRefs = useRef<Record<string, SVGGElement | null>>({})

    // Force a re-render once the DOM is painted (allows for the g elements defining
    // drag-drop groups to actually exist)
    const [, setIsMounted] = useState(false)
    useEffect(() => {
        setIsMounted(true)
    }, [])

    useEffect(() => {
        setDraftSelected(selected)
    }, [selected])

    const handleApplyRef = useRef(() => onAdd(draftSelected))

    useEffect(() => {
        handleApplyRef.current = () => onAdd(draftSelected)
    })

    useEffect(() => {
        if (!statRef.current) {
            return
        }

        statRef.current.addEventListener('toggle', (e) => {
            if (e.newState !== 'closed') {
                return
            }

            handleApplyRef.current()
        })
    }, [statRef])

    const dimensions = useDimensions()
    const { isMobile } = dimensions

    const isFull = isStatsFull(
        draftSelected,
        currentType,
        showTitle,
        showTeams,
        false,
        dimensions
    )

    const filteredStats = stats.filter((d) => {
        if (searchFilter === '') {
            return true
        }

        if (!prettyStatNames[d]) {
            return true
        }

        return prettyStatNames[d]
            .toLowerCase()
            .includes(searchFilter.toLowerCase())
    })

    const sensors = useMemo(
        () => [
            PointerSensor.configure({
                activationConstraints: [
                    new PointerActivationConstraints.Distance({ value: 8 }),
                    new PointerActivationConstraints.Delay({
                        value: 200,
                        tolerance: 10,
                    }),
                ],
            }),
        ],
        []
    )

    type DragOverEvent = Parameters<
        NonNullable<React.ComponentProps<typeof DragDropProvider>['onDragOver']>
    >[0]

    const handleDragOver = (event: DragOverEvent) => {
        const { operation } = event
        const { source, target } = operation
        if (isSortable(source) && isSortable(target)) {
            const overIndex = target.sortable.index
            const activeIndex = source.sortable.index
            if (activeIndex !== overIndex) {
                setDraftSelected((prev) =>
                    arrayMove([...prev], activeIndex, overIndex)
                )
            }
        }
    }

    const handleToggleStat = (stat: string) => {
        const isSelected = draftSelected.includes(stat as keyof PlayerRow)

        // If it's not already selected and the picker is full, don't allow adding.
        if (!isSelected && isFull) {
            return
        }

        if (isSelected) {
            setDraftSelected((prev) => prev.filter((s) => s !== stat))
        } else {
            setFlashingStat(stat)
            setTimeout(() => setFlashingStat(null), 150)
            setDraftSelected((prev) => [...prev, stat as keyof PlayerRow])
        }
    }

    const groupedStats = groupStatSelects(filteredStats)

    const handleSelectDefaults = useCallback(
        (defaultStats: string[]) => {
            const availableStats = stats as string[]
            const applicableDefaults = defaultStats.filter((stat) =>
                availableStats.includes(stat)
            )
            setDraftSelected(applicableDefaults as (keyof PlayerRow)[])
        },
        [stats]
    )

    const groupedDraft = groupStatSelects(draftSelected as (keyof PlayerRow)[])
    const existingDraftTypes = (
        Object.keys(groupedDraft) as StatGroup[]
    ).filter((type) => groupedDraft[type].length > 0)

    // Calculate dynamic SVG height: 35px per stat + 25px per header + 10px buffer
    const ROW_HEIGHT = 35
    const HEADER_HEIGHT = 25
    const rightSideSvgHeight =
        draftSelected.length * ROW_HEIGHT +
        existingDraftTypes.length * HEADER_HEIGHT +
        10

    return (
        <div
            {...({
                popover: 'auto',
            } as React.HTMLAttributes<HTMLDivElement>)}
            id="stat-search"
            ref={statRef}
            className="stat-picker-popover comp-tool-popover"
        >
            <style>{`
                ${
                    isMobile
                        ? `
                    .stat-picker-popover {
                        width: 95vw !important;
                        padding: 10px;
                    }
                    .stat-picker-overall-wrapper {
                        gap: 10px; 
                    }
                    .stat-picker-left-side-wrapper {
                        flex: 0 0 140px;
                    }
                    .stat-picker-right-side-wrapper {
                        flex: 1;
                        min-width: 0;
                    }
                    .stat-picker-button-wrapper {
                        flex-direction: column; 
                        gap: 2px;
                    }
                    .stat-picker-default-button {
                        font-size: 10px; 
                        padding: 4px;
                        margin-top: 0;
                    }
                    .stat-picker-scroll-element {
                        font-size: 10px;
                        padding: 6px 10px 4px; 
                        word-break: break-word;
                    }
                    #stat-picker-search-bar {
                        font-size: 10px;
                    }
                `
                        : ''
                }
            `}</style>

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                }}
            >
                <h3 style={{ margin: '5px' }}>Choose & Order Stats</h3>
                <button
                    type="button"
                    onClick={() => {
                        const popover = document.getElementById('stat-search')
                        if (popover && 'hidePopover' in popover) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ;(popover as any).hidePopover()
                        }
                    }}
                    className="modal-close-button"
                    aria-label="Close Popover"
                >
                    &times;
                </button>
            </div>

            <div className="stat-picker-overall-wrapper">
                <div className="stat-picker-left-side-wrapper">
                    <div className="stat-picker-button-wrapper">
                        <button
                            type="button"
                            className="btn btn-default stat-picker-default-button"
                            onClick={() =>
                                handleSelectDefaults(DEFAULT_BATTING_STATS)
                            }
                        >
                            Batting Defaults
                        </button>
                        <button
                            type="button"
                            className="btn btn-default stat-picker-default-button"
                            onClick={() =>
                                handleSelectDefaults(DEFAULT_PITCHING_STATS)
                            }
                        >
                            Pitching Defaults
                        </button>
                        <button
                            type="button"
                            className="btn btn-default stat-picker-default-button"
                            onClick={() =>
                                handleSelectDefaults(DEFAULT_CATCHING_STATS)
                            }
                        >
                            Catching Defaults
                        </button>
                    </div>
                    <input
                        className="form-control comp-tool-search"
                        id="stat-picker-search-bar"
                        type="text"
                        placeholder="Search by stat name"
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                    />

                    <div className="stat-picker-scroll-menu">
                        {Object.keys(groupedStats)
                            .filter((key) => {
                                if (currentType === 'batter') {
                                    return [
                                        'batting',
                                        'fielding',
                                        'catching',
                                        'running',
                                        'value',
                                    ].includes(key)
                                }
                                return [
                                    'batting',
                                    'pitching',
                                    'value',
                                ].includes(key)
                            })
                            .map((groupKey) => {
                                const key = groupKey as StatGroup
                                const statsInGroup = groupedStats[key]

                                // Only render the group if it contains stats matching the filter
                                if (statsInGroup.length === 0) {
                                    return null
                                }

                                return (
                                    <div key={key}>
                                        <div className="stat-picker-scroll-subheader">
                                            {key === 'batting'
                                                ? 'BATTING/PITCHING'
                                                : key}
                                        </div>

                                        {statsInGroup.map((stat) => {
                                            const isSelected =
                                                draftSelected.includes(stat)
                                            const isFlashing =
                                                flashingStat === stat

                                            let backgroundColor = 'white'
                                            if (isSelected)
                                                backgroundColor = '#e6e6e6'
                                            if (isFlashing)
                                                backgroundColor =
                                                    'rgba(57, 144, 152, 0.4)'

                                            return (
                                                <div
                                                    key={stat}
                                                    onClick={() =>
                                                        handleToggleStat(
                                                            stat as string
                                                        )
                                                    }
                                                    className="stat-picker-scroll-element"
                                                    style={{
                                                        cursor:
                                                            isSelected || isFull
                                                                ? 'default'
                                                                : 'pointer',
                                                        background:
                                                            backgroundColor,
                                                        opacity: isSelected
                                                            ? 0.6
                                                            : 1,
                                                    }}
                                                >
                                                    {stat ===
                                                        'percent_rank_swing_take_run_value' &&
                                                    currentType === 'pitcher'
                                                        ? 'Pitching'
                                                        : prettyStatNames[stat]}
                                                </div>
                                            )
                                        })}
                                    </div>
                                )
                            })}
                    </div>
                </div>

                <div className="stat-picker-right-side-wrapper">
                    <label className="stat-picker-right-side-title">
                        Selected Stats (drag to reorder)
                    </label>
                    <div className="stat-picker-right-side-scroll">
                        <DragDropProvider
                            sensors={sensors}
                            onDragOver={handleDragOver}
                            modifiers={[RestrictToVerticalAxis]}
                            plugins={(defaults) =>
                                defaults.filter(
                                    (plugin) => plugin !== AutoScroller
                                )
                            }
                        >
                            <svg
                                style={{
                                    width: '100%',
                                    height: `${rightSideSvgHeight}px`,
                                }}
                            >
                                {(() => {
                                    let currentY = 5
                                    return existingDraftTypes.map((type) => {
                                        const groupStats = groupedDraft[type]
                                        if (groupStats.length === 0) {
                                            return null
                                        }

                                        const headerY = currentY
                                        currentY += HEADER_HEIGHT

                                        const groupStartY = currentY

                                        const statNodes = groupStats.map(
                                            (stat, i) => {
                                                const globalIndex =
                                                    draftSelected.indexOf(stat)
                                                const relativeY = i * ROW_HEIGHT
                                                const prettyStatName =
                                                    stat ===
                                                        'percent_rank_swing_take_run_value' &&
                                                    currentType === 'pitcher'
                                                        ? 'Pitching'
                                                        : prettyStatNames[stat]

                                                return (
                                                    <SortableWrapper
                                                        key={stat}
                                                        id={stat}
                                                        index={globalIndex}
                                                        offset={relativeY}
                                                        groupName={`draft-stats-${type}`}
                                                        type="stat"
                                                        container={
                                                            groupRefs.current[
                                                                type
                                                            ]
                                                        }
                                                    >
                                                        <rect
                                                            width={
                                                                STAT_DRAG_RECT_WIDTH
                                                            }
                                                            height={30}
                                                            x={8}
                                                            rx={4}
                                                            className="stat-picker-sortable-rect"
                                                        />
                                                        <text
                                                            x={16}
                                                            y={20}
                                                            className="stat-picker-sortable-text"
                                                        >
                                                            {prettyStatName.length >
                                                            TRUNCATE_TEXT_CUTOFF
                                                                ? `${prettyStatName.substring(
                                                                      0,
                                                                      TRUNCATE_TEXT_LENGTH
                                                                  )}...`
                                                                : prettyStatName}
                                                        </text>
                                                        <rect
                                                            x={8}
                                                            y={0}
                                                            width={
                                                                CLICK_AREA_WIDTH
                                                            }
                                                            height={30}
                                                            fill="transparent"
                                                        />
                                                        <g
                                                            onPointerDown={(
                                                                e
                                                            ) =>
                                                                e.stopPropagation()
                                                            }
                                                            onMouseDown={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handleToggleStat(
                                                                    stat
                                                                )
                                                            }}
                                                            style={{
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <RemoveButton
                                                                x={
                                                                    REMOVE_BUTTON_X
                                                                }
                                                                y={15}
                                                                symbol="×"
                                                            />
                                                        </g>
                                                    </SortableWrapper>
                                                )
                                            }
                                        )

                                        currentY +=
                                            groupStats.length * ROW_HEIGHT

                                        return (
                                            <g key={`draft-group-${type}`}>
                                                <text
                                                    x={8}
                                                    y={headerY + 16}
                                                    className="stat-picker-drag-header-text"
                                                >
                                                    {type === 'batting'
                                                        ? 'BATTING/PITCHING'
                                                        : type}
                                                </text>

                                                <g
                                                    ref={(el) => {
                                                        groupRefs.current[
                                                            type
                                                        ] = el
                                                    }}
                                                    transform={`translate(0, ${groupStartY})`}
                                                >
                                                    {statNodes}
                                                </g>
                                            </g>
                                        )
                                    })
                                })()}
                            </svg>
                        </DragDropProvider>
                    </div>
                </div>
            </div>

            {isFull ? (
                <div
                    style={{
                        textAlign: 'center',
                        color: 'red',
                        marginTop: '4px',
                        fontSize: '12px',
                    }}
                >
                    <text>
                        Your stats menu is full. To add other stats, deselect
                        one or more selected items.
                    </text>
                </div>
            ) : null}

            <div
                style={{
                    marginTop: isFull ? '4px' : '15px',
                    textAlign: 'center',
                }}
            >
                <button
                    type="button"
                    className="btn btn-default"
                    onClick={() => {
                        statRef.current?.hidePopover()
                        onAdd(draftSelected as string[])
                    }}
                >
                    Apply Changes
                </button>
            </div>
        </div>
    )
}
